import { mergeChatOverview } from '../../../services/whatsapp/mergeChatOverview';
import { reconcileUnreadWithWaha } from '../../../services/whatsapp/wahaReadState';
import { friendlyDbError } from '../../../utils/friendlyDbError';
/**
 * Chat list slice — gestisce la lista chat (load, loadMore, refresh, addChatToStore, loadAllChatsCache).
 * Estratto da whatsappChatStore.ts.
 */

import type { StateCreator } from 'zustand';
import type { ChatListSlice, WhatsAppChatStore, ChatOverview } from '../types';
import { supabase } from '../../../supabase';
import { getWahaService } from '../../../services/wahaApiService';
import { debugLog } from '../../../../components/whatsapp2/chat/ChatDebugPanel';
import {
  enrichGroupNames,
  enrichLidNames,
  enrichSenderNames,
  enrichChatNames,
  setGroupNamesCache,
  getGroupNamesCache,
  getGroupNamesCacheTimestamp,
  setGroupNamesCacheTimestamp,
  getContactsCacheTimestamp,
  setContactsCacheTimestamp,
  GROUP_NAMES_CACHE_TTL,
  CONTACTS_CACHE_TTL,
} from '../caches';
import { filterLidDuplicates, handleLidResolution } from '../lidResolution';
import { WhatsAppDbService } from '../../../services/whatsappDbService';

/** Massimo di chat che WAHA restituisce per chiamata a /chats/overview. */
const WAHA_OVERVIEW_PAGE = 100;

// Cache nomi da DB CRM — evita query ogni 5s durante refreshChats
let _dbNamesCache = new Map<string, string>();
let _dbNamesCacheTimestamp = 0;
const DB_NAMES_CACHE_TTL = 5 * 60 * 1000; // 5 minuti

/** Le bozze locali (chat create dal CRM, mai scritte) sopravvivono al ricaricamento della lista dal server. */
function conBozze(prev: ChatOverview[], next: ChatOverview[]): ChatOverview[] {
  const bozze = prev.filter((c) => c.isDraft && !next.some((n) => n.id === c.id));
  return bozze.length ? [...bozze, ...next] : next;
}

export const createChatListSlice: StateCreator<
  WhatsAppChatStore,
  [],
  [],
  ChatListSlice
> = (set, get) => ({
  // ============ INITIAL STATE ============
  chats: [],
  chatsLoading: false,
  chatsError: null,
  _loadChatsAttempt: 0,
  chatsOffset: 0,
  chatsHasMore: true,
  chatsLoadingMore: false,
  allChatsCache: null,
  allChatsCacheTimestamp: 0,

  // ============ ACTIONS ============
  loadChats: async () => {
    const { wahaConfig, chatsLoading } = get();
    if (!wahaConfig) return;
    // Guard: evita doppia chiamata se già in caricamento
    if (chatsLoading) {
      debugLog('ChatList', 'loadChats: SKIP (già in caricamento)', null, 'warn');
      return;
    }

    // ── Branch CLOUD: le chat vengono dal DB (whatsapp_messages), non da WAHA API ──
    const provider = get().provider;
    if (provider && provider.type !== 'whatsapp') {
      set({ chatsLoading: true, chatsError: null });
      try {
        const cloudChats = await WhatsAppDbService.loadCloudChatsFromDb(
          wahaConfig.configId,
          wahaConfig.organizationId,
        );
        if (get().wahaConfig !== wahaConfig) return;
        set({
          chats: conBozze(get().chats, cloudChats),
          chatsLoading: false,
          chatsOffset: 0,
          chatsHasMore: false, // Cloud: lista chat completa dal DB (no paginazione API)
          chatsLoadedForConfig: wahaConfig.configId,
          _loadChatsAttempt: 0,
        });
        supabase.auth.getSession().then(({ data }) => {
          if (data?.session?.user?.id) get().loadUnreadCountsFromDb(data.session.user.id);
        });
      } catch (error) {
        if (get().wahaConfig !== wahaConfig) return;
        console.error('[Store] loadChats (cloud) error:', error);
        set({
          chatsError: friendlyDbError(error) || 'Errore caricamento chat',
          chatsLoading: false,
        });
      }
      return;
    }

    // Se non abbiamo chat, ripristina dal backup prima di mostrare loading
    if (get().chats.length === 0) {
      try {
        const backup = sessionStorage.getItem('whatsapp_chats_backup');
        if (backup) {
          const restored = JSON.parse(backup);
          if (Array.isArray(restored) && restored.length > 0) {
            set({ chats: restored });
            console.debug('[Store] Ripristinate', restored.length, 'chat dal backup sessionStorage');
          }
        }
      } catch { /* ignore */ }
    }

    set({ chatsLoading: true, chatsError: null });
    debugLog('ChatList', 'loadChats: inizio caricamento...');

    try {
      const service = getWahaService();
      const [chats, groups] = await Promise.all([
        service.getChatsOverview(500, 0),
        service.getAllGroups().catch((err) => {
          console.error('[Store] getAllGroups FAILED:', err);
          return [] as Array<{ id: string; name: string; isGroup: true }>;
        }),
      ]);

      debugLog('ChatList', `loadChats: ${chats.length} chat + ${groups.length} gruppi da WAHA`, {
        totalChats: chats.length,
        totalGroups: groups.length,
        groupsSample: groups.slice(0, 5).map(g => ({ id: g.id.slice(0, 25), name: g.name })),
        chatsSample: chats.slice(0, 5).map(c => ({ id: c.id.slice(0, 25), name: c.name, isGroup: c.isGroup })),
      }, 'data');

      if (get().wahaConfig !== wahaConfig) return;
      if (groups.length > 0) {
        setGroupNamesCache(new Map(groups.map(g => [g.id, g.name])));
        setGroupNamesCacheTimestamp(Date.now());
      }

      // Carica contacts cache per risolvere nomi sender nei gruppi e chat @lid
      // transformChat gira DENTRO getChatsOverview, prima che la cache sia pronta
      // → re-enrich qui dopo che la cache è disponibile
      const contactsMap = await service.getContactsMap().catch(() => new Map<string, string>());
      if (get().wahaConfig !== wahaConfig) return;
      if (contactsMap.size > 0) setContactsCacheTimestamp(Date.now());

      let enriched = filterLidDuplicates(
        enrichChatNames(enrichLidNames(enrichSenderNames(enrichGroupNames(chats), contactsMap), contactsMap), contactsMap),
        get().lidToCusMap,
        (resolved) => {
          if (get().wahaConfig !== wahaConfig) return;
          const filtered = handleLidResolution(resolved, get().chats, get().lidToCusMap, get().saveLidMapping);
          if (filtered) set({ chats: filtered });
        },
      );

      // Fallback DB: cerca nomi nel CRM per chat @c.us ancora senza nome
      const chatsWithoutName = enriched.filter(c =>
        c.id.endsWith('@c.us') && /^\+?\d[\d\s\-()]{5,}$/.test(c.name)
      );
      if (chatsWithoutName.length > 0 && wahaConfig.organizationId) {
        const dbNames = await WhatsAppDbService.lookupContactNames(
          chatsWithoutName.map(c => c.id), wahaConfig.organizationId
        ).catch(() => new Map<string, string>());
        if (dbNames.size > 0) {
          enriched = enriched.map(c => {
            const dbName = dbNames.get(c.id);
            return dbName ? { ...c, name: dbName } : c;
          });
          // Salva nella cache per refreshChats (evita query DB ogni 5s)
          _dbNamesCache = dbNames;
          _dbNamesCacheTimestamp = Date.now();
        }
      }

      // Log chat con isGroup che potrebbero avere problemi
      const groupChats = enriched.filter(c => c.isGroup);
      const chatsWith120363 = enriched.filter(c => c.id.startsWith('120363'));
      debugLog('ChatList', `loadChats: enriched ${enriched.length} chat (${groupChats.length} gruppi, ${chatsWith120363.length} community)`, {
        groupsSample: groupChats.slice(0, 5).map(c => ({ id: c.id.slice(0, 25), name: c.name, isGroup: c.isGroup })),
        communitySample: chatsWith120363.slice(0, 5).map(c => ({ id: c.id.slice(0, 25), name: c.name, isGroup: c.isGroup })),
      }, 'data');

      if (get().wahaConfig !== wahaConfig) return;
      // Non sovrascrivere con array vuoto se avevamo già chat (WAHA temporaneamente instabile)
      if (enriched.length > 0 || get().chats.length === 0) {
        // Prima connessione: se WAHA sta ancora sincronizzando (0 chat), mantieni loading e riprova
        if (enriched.length === 0) {
          const attempt = get()._loadChatsAttempt;
          if (attempt < 6) { // max 6 tentativi = ~2+4+8+15+15+15 = ~59s
            const nextAttempt = attempt + 1;
            const delay = Math.min(2000 * Math.pow(2, attempt), 15000);
            debugLog('ChatList', `loadChats: 0 chat (tentativo ${nextAttempt}/6), retry in ${delay}ms`);
            set({ _loadChatsAttempt: nextAttempt });
            setTimeout(() => {
              if (get().wahaConfig !== wahaConfig) return;
              set({ chatsLoading: false }); // sblocca il guard anti-doppia chiamata
              get().loadChats();
            }, delay);
            return; // mantieni chatsLoading: true
          }
          // Dopo 6 tentativi: mostra stato sincronizzazione
          set({ chatsLoading: false, chatsError: 'sync_in_progress' });
          debugLog('ChatList', 'loadChats: 0 chat dopo 6 tentativi, mostro stato sync', null, 'warn');
          return;
        }

        // Chat arrivate: reset retry counter
        // WAHA risponde al massimo 100 chat per chiamata anche se se ne chiedono
        // 500: con `>= 500` la pagina successiva non veniva mai richiesta e la
        // lista si fermava a 100 (visto in prod il 02/09/2026). L'offset e' il
        // numero di chat davvero ricevute, "altre" = pagina piena.
        set({
          chats: mergeChatOverview(get().chats, enriched),
          chatsLoading: false,
          chatsOffset: chats.length,
          chatsHasMore: chats.length >= WAHA_OVERVIEW_PAGE,
          chatsLoadedForConfig: wahaConfig.configId,
          _loadChatsAttempt: 0,
        });
        // Backup in sessionStorage per sopravvivere a tab switch
        if (enriched.length > 0) {
          try { sessionStorage.setItem('whatsapp_chats_backup', JSON.stringify(enriched.slice(0, 100))); } catch { /* ignore */ }
        }
        debugLog('ChatList', `loadChats: completato, ${enriched.length} chat nello store`);

        // Carica unread counts dal DB immediatamente dopo le chat
        // (non aspettiamo il mount della Sidebar — così le chat escono già con gli unread)
        supabase.auth.getSession().then(({ data }) => {
          if (data?.session?.user?.id) {
            get().loadUnreadCountsFromDb(data.session.user.id);
          }
        });
      } else {
        set({ chatsLoading: false });
        debugLog('ChatList', 'loadChats: 0 chat enriched, mantenute le precedenti', null, 'warn');
      }
    } catch (error) {
      if (get().wahaConfig !== wahaConfig) return;
      console.error('[Store] loadChats error:', error);
      debugLog('ChatList', `loadChats ERRORE: ${error instanceof Error ? error.message : String(error)}`, null, 'error');
      set({
        chatsError: friendlyDbError(error) || 'Errore caricamento chat',
        chatsLoading: false,
      });
    }
  },

  loadMoreChats: async () => {
    const { wahaConfig, chatsOffset, chatsHasMore, chatsLoadingMore, chats } = get();

    if (!wahaConfig || !chatsHasMore || chatsLoadingMore) return;

    set({ chatsLoadingMore: true });

    try {
      const service = getWahaService();
      // Pagina successiva a partire dalle chat gia' ricevute (WAHA ne da' al
      // massimo WAHA_OVERVIEW_PAGE per chiamata).
      const newOffset = chatsOffset;
      const newChats = await service.getChatsOverview(500, newOffset);

      if (get().wahaConfig !== wahaConfig) return;
      if (newChats.length === 0) {
        set({ chatsHasMore: false, chatsLoadingMore: false });
        return;
      }

      // Arricchisci nomi gruppi e deduplica
      const enrichedNewChats = filterLidDuplicates(enrichGroupNames(newChats), get().lidToCusMap);
      if (get().wahaConfig !== wahaConfig) return;
      const currentChats = get().chats;
      const existingIds = new Set(currentChats.map((c) => c.id));
      const uniqueNewChats = enrichedNewChats.filter((c) => !existingIds.has(c.id));

      set({
        chats: mergeChatOverview(currentChats, uniqueNewChats),
        chatsOffset: newOffset + newChats.length,
        chatsHasMore: newChats.length >= WAHA_OVERVIEW_PAGE,
        chatsLoadingMore: false,
      });
    } catch (error) {
      if (get().wahaConfig !== wahaConfig) return;
      console.error('[Store] loadMoreChats error:', error);
      set({ chatsLoadingMore: false });
    }
  },

  refreshChats: async () => {
    // Refresh senza loader per non bloccare UI
    const { wahaConfig, chats, chatsOffset } = get();
    if (!wahaConfig) return;

    // ── Branch CLOUD: niente WAHA. Prima il refresh periodico della barra laterale
    //    chiamava comunque il servizio WAHA e, su un numero Cloud, riempiva la lista
    //    con le chat dell'ultima sessione WAHA aperta (visto il 07/09/2026). ──
    const provider = get().provider;
    if (provider && provider.type !== 'whatsapp') {
      try {
        const cloudChats = await WhatsAppDbService.loadCloudChatsFromDb(wahaConfig.configId, wahaConfig.organizationId);
        if (get().wahaConfig !== wahaConfig) return;
        const prev = get().chats;
        const selezionata = get().selectedChatId;
        const unreadPrima = new Map(prev.map((c) => [c.id, c.unreadCount]));
        const aggiornate = cloudChats.map((c) => ({
          ...c,
          unreadCount: c.id === selezionata ? 0 : (unreadPrima.get(c.id) ?? c.unreadCount),
        }));
        set({ chats: conBozze(prev, aggiornate), chatsLoadedForConfig: wahaConfig.configId });
      } catch (error) {
        console.error('[Store] refreshChats (cloud) error:', error);
      }
      return;
    }

    try {
      const service = getWahaService();
      // Ricarica almeno tante chat quante ne abbiamo già (o minimo 500)
      const currentCount = chats.length;
      const minToLoad = Math.max(500, chatsOffset + 500, currentCount);

      // Refresh cache nomi gruppi e contatti solo se scadute (ogni 5 min, non ogni 15s)
      const shouldRefreshGroups = Date.now() - getGroupNamesCacheTimestamp() > GROUP_NAMES_CACHE_TTL;
      const shouldRefreshContacts = Date.now() - getContactsCacheTimestamp() > CONTACTS_CACHE_TTL;

      const [refreshedChats, groups] = await Promise.all([
        service.getChatsOverview(minToLoad, 0),
        shouldRefreshGroups
          ? service.getAllGroups().catch(() => [] as Array<{ id: string; name: string; isGroup: true }>)
          : Promise.resolve(null),
      ]);

      if (get().wahaConfig !== wahaConfig) return;
      if (groups && groups.length > 0) {
        setGroupNamesCache(new Map(groups.map(g => [g.id, g.name])));
        setGroupNamesCacheTimestamp(Date.now());
      }

      // Refresh contacts cache se scaduta (per nomi sender e @lid)
      if (shouldRefreshContacts) {
        service.invalidateContactsCache();
      }
      const contactsMap = await service.getContactsMap().catch(() => new Map<string, string>());
      if (get().wahaConfig !== wahaConfig) return;
      if (contactsMap.size > 0 && shouldRefreshContacts) setContactsCacheTimestamp(Date.now());

      // In refreshChats NON lanciare risoluzione API (solo cache + nome)
      // La risoluzione API avviene solo in loadChats per evitare chiamate ripetute
      let enriched = filterLidDuplicates(
        enrichChatNames(enrichLidNames(enrichSenderNames(enrichGroupNames(refreshedChats), contactsMap), contactsMap), contactsMap),
        get().lidToCusMap,
      );

      // Fallback DB: applica cache nomi CRM (caricata in loadChats, refresh ogni 5 min)
      if (_dbNamesCache.size > 0) {
        // Refresh cache DB se scaduta
        if (Date.now() - _dbNamesCacheTimestamp > DB_NAMES_CACHE_TTL && wahaConfig.organizationId) {
          const chatsWithoutName = enriched.filter(c =>
            c.id.endsWith('@c.us') && /^\+?\d[\d\s\-()]{5,}$/.test(c.name)
          );
          if (chatsWithoutName.length > 0) {
            const dbNames = await WhatsAppDbService.lookupContactNames(
              chatsWithoutName.map(c => c.id), wahaConfig.organizationId
            ).catch(() => new Map<string, string>());
            if (dbNames.size > 0) {
              _dbNamesCache = dbNames;
              _dbNamesCacheTimestamp = Date.now();
            }
          }
        }
        enriched = enriched.map(c => {
          const dbName = _dbNamesCache.get(c.id);
          return (dbName && /^\+?\d[\d\s\-()]{5,}$/.test(c.name)) ? { ...c, name: dbName } : c;
        });
      }

      if (get().wahaConfig !== wahaConfig) return;
      // Non sovrascrivere con array vuoto se avevamo già chat (WAHA temporaneamente instabile)
      if (enriched.length > 0 || get().chats.length === 0) {
        const prevChats = get().chats;
        const prevCount = prevChats.length;

        // unreadCount: ignoriamo il conteggio da WAHA (NOWEB inaffidabile), salvo lo 0
        // "chat letta" applicato sotto dopo il merge.
        // La sorgente di verità è il DB Supabase via loadUnreadCountsFromDb,
        // più gli increment istantanei dal realtime in addMessage().
        // Qui preserviamo solo il valore locale precedente (incrementato dal realtime
        // o sovrascritto dal polling DB).
        const prevUnreadMap = new Map<string, number>();
        for (const c of prevChats) {
          if (c.unreadCount != null) {
            prevUnreadMap.set(c.id, c.unreadCount);
          }
        }
        const currentSelected = get().selectedChatId;
        const merged = enriched.map(c => {
          // Chat aperta → sempre 0
          if (c.id === currentSelected) return { ...c, unreadCount: 0 };
          // Altrimenti: usa il valore locale precedente (può essere 0 o >0)
          const prev = prevUnreadMap.get(c.id);
          return { ...c, unreadCount: prev ?? 0 };
        });

        // Rileva riordinamento: confronta le prime 5 posizioni
        const prevTop5 = prevChats.slice(0, 5).map(c => c.id);
        const newTop5 = merged.slice(0, 5).map(c => c.id);
        const orderChanged = prevTop5.some((id, i) => id !== newTop5[i]);

        // 🚨 7 set 2026: qui si sostituiva la lista con le sole chat appena ricaricate.
        // Ma WAHA tronca `/chats/overview` a 100 risultati per chiamata, e questo
        // refresh gira ogni 15 secondi: tutto ciò che l'utente aveva caricato con
        // "carica altre" spariva da solo nel giro di pochi secondi. È la segnalazione
        // del 27/03 ("non carica tutte le chat, alcune ci sono ma non sono mostrate").
        // Le chat già note che non rientrano nella finestra ricaricata vengono ora
        // mantenute e riordinate insieme: possono avere messaggi realtime piu recenti.
        // Unica eccezione al valore locale: WAHA dice 0 non letti (chat letta dal
        // telefono) e non è arrivato nulla dopo -> 0. Il DB si riallinea in
        // loadUnreadCountsFromDb.
        const listaCompleta = mergeChatOverview(prevChats, merged).map(c => {
          const unread = c.unreadCount ?? 0;
          const riconciliato = reconcileUnreadWithWaha(unread, c);
          return riconciliato === unread ? c : { ...c, unreadCount: riconciliato };
        });
        set({ chats: listaCompleta });
        // Aggiorna backup
        if (merged.length > 0) {
          try { sessionStorage.setItem('whatsapp_chats_backup', JSON.stringify(merged.slice(0, 100))); } catch { /* ignore */ }
        }

        if (orderChanged) {
          // Trova quale chat è salita in cima
          const newFirst = merged[0];
          const prevPos = prevChats.findIndex(c => c.id === newFirst?.id);
          debugLog('ChatList', `RIORDINAMENTO: "${newFirst?.name}" da pos ${prevPos} → 0`, {
            prevTop5: prevTop5.map((id, i) => prevChats[i]?.name || id.slice(0, 20)),
            newTop5: newTop5.map((id, i) => merged[i]?.name || id.slice(0, 20)),
          }, 'warn');
        }

        if (merged.length !== prevCount) {
          debugLog('ChatList', `refreshChats: ${prevCount} → ${merged.length} chat`);
        }
      }
    } catch (err) {
      console.warn('[Store] refreshChats error:', err);
      debugLog('ChatList', `refreshChats ERRORE: ${err instanceof Error ? err.message : String(err)}`, null, 'error');
    }
  },

  addChatToStore: (chat: ChatOverview) => {
    const { chats } = get();
    if (!chats.find(c => c.id === chat.id)) {
      const updated = [...chats, chat].sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return (b.lastMessage?.timestamp || 0) - (a.lastMessage?.timestamp || 0);
      });
      set({ chats: updated });
    }
  },

  loadAllChatsCache: async () => {
    const { wahaConfig, allChatsCache, allChatsCacheTimestamp } = get();
    if (!wahaConfig) return;

    // Cache valida per 5 minuti
    const CACHE_TTL = 5 * 60 * 1000;
    if (allChatsCache && (Date.now() - allChatsCacheTimestamp) < CACHE_TTL) return;

    try {
      const service = getWahaService();
      // Carica chat, contatti e gruppi in parallelo
      const [allChats, allContacts, allGroups] = await Promise.all([
        service.getAllChatsLight(),
        service.getAllContacts().catch(() => [] as Array<{ id: string; name: string; isGroup: false }>),
        service.getAllGroups().catch((err) => {
          console.error('[Store] getAllGroups FAILED:', err);
          return [] as Array<{ id: string; name: string; isGroup: true }>;
        }),
      ]);

      // Aggiorna cache nomi gruppi (fonte autoritativa)
      if (allGroups.length > 0) {
        setGroupNamesCache(new Map(allGroups.map(g => [g.id, g.name])));
        setGroupNamesCacheTimestamp(Date.now());
      }

      // Arricchisci nomi gruppi nelle chat da /chats/overview
      const groupNamesCache = getGroupNamesCache();
      // Costruisci contactsMap per enrichment nomi chat @c.us
      const contactsMapForCache = new Map<string, string>();
      for (const c of allContacts) {
        const phoneNumber = c.id.split('@')[0];
        if (c.name && c.name !== phoneNumber) {
          contactsMapForCache.set(c.id, c.name);
          if (!contactsMapForCache.has(phoneNumber)) contactsMapForCache.set(phoneNumber, c.name);
        }
      }
      const enrichedChats = allChats.map(c => {
        if (c.id.endsWith('@g.us') && groupNamesCache.has(c.id)) {
          return { ...c, name: groupNamesCache.get(c.id)! };
        }
        // Arricchisci nomi chat 1:1 @c.us (WAHA NOWEB restituisce numero come nome)
        if (c.id.endsWith('@c.us')) {
          const phoneNumber = c.id.split('@')[0];
          if (c.name === phoneNumber || c.name === c.id || c.name === `+${phoneNumber}`) {
            const name = contactsMapForCache.get(c.id) || contactsMapForCache.get(phoneNumber);
            if (name) return { ...c, name };
          }
        }
        return c;
      });

      // Merge: chat hanno priorità, poi gruppi, poi contatti (deduplicati per id)
      const ids = new Set(enrichedChats.map(c => c.id));
      const uniqueGroups = allGroups.filter(g => !ids.has(g.id));
      uniqueGroups.forEach(g => ids.add(g.id));
      const uniqueContacts = allContacts.filter(c => !ids.has(c.id));
      let merged = [...enrichedChats, ...uniqueGroups, ...uniqueContacts];

      // Applica nomi DB CRM (dalla cache o nuova query)
      if (_dbNamesCache.size > 0) {
        merged = merged.map(c => {
          const dbName = _dbNamesCache.get(c.id);
          if (dbName && c.id.endsWith('@c.us')) {
            const phoneNumber = c.id.split('@')[0];
            if (c.name === phoneNumber || c.name === c.id || /^\+?\d[\d\s\-()]{5,}$/.test(c.name)) {
              return { ...c, name: dbName };
            }
          }
          return c;
        });
      }

      set({ allChatsCache: merged, allChatsCacheTimestamp: Date.now() });
      console.log(`[Store] Cached ${merged.length} entries for search (${allChats.length} chats + ${uniqueGroups.length} groups + ${uniqueContacts.length} contacts)`);
    } catch (error) {
      console.error('[Store] loadAllChatsCache error:', error);
    }
  },
});
